import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Users, Zap, Gift, Coins, Trophy, Ticket, Star } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    { icon: TrendingUp, title: "Mise sur des artistes", desc: "Soutiens des artistes avec des credits gratuits. Si leur cote monte, tes credits se multiplient." },
    { icon: Users, title: "Visibilite maximale", desc: "Chaque publication est partagee a toute la communaute. Pas d'algorithme cache." },
    { icon: Zap, title: "Gagne des credits", desc: "Like, commente, decouvre. Chaque interaction te rapporte des credits. Aucun paiement requis." },
    { icon: Gift, title: "Encheres exclusives", desc: "Utilise tes credits pour encherir sur des goodies uniques proposes par les artistes." },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="landing-page">
      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF6B35] opacity-[0.08] blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#7000FF] opacity-[0.06] blur-[100px]" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Nav */}
        <nav className="flex items-center justify-between mb-12 sm:mb-16 md:mb-24">
          <span className="font-heading font-bold text-2xl sm:text-3xl text-gradient" data-testid="landing-logo">ReallyFan</span>
          <button
            onClick={() => navigate("/auth")}
            className="text-sm font-medium text-[#AFA8BA] hover:text-[#FF6B35] transition-colors px-4 py-2 rounded-full border border-foreground/10 hover:border-[#FF6B35]/30"
            data-testid="landing-login-btn"
          >
            Se connecter
          </button>
        </nav>

        {/* FREE badge - first thing you see */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide" data-testid="free-badge">
            <Coins className="w-4 h-4" />
            100% GRATUIT — 100 credits offerts
          </span>
        </motion.div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl text-foreground leading-[1.05] mb-5 sm:mb-6 max-w-3xl">
            Soutiens des artistes.{" "}
            <span className="text-gradient">Gagne des credits.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#AFA8BA] max-w-xl mb-4 leading-relaxed">
            Le reseau social ou chaque like, chaque decouverte et chaque concert te rapporte.
            Mise sur les artistes de demain et recolte les fruits de ton flair.
          </p>
          <p className="text-sm sm:text-base text-emerald-400 font-bold mb-8 sm:mb-10 flex items-center gap-2">
            <Star className="w-4 h-4 fill-emerald-400" />
            Inscription gratuite + 100 credits offerts. Pas de carte bancaire.
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <button
              onClick={() => navigate("/auth?tab=register")}
              className="bg-[#FF6B35] text-white font-bold px-6 sm:px-8 py-3.5 rounded-full shadow-[0_0_30px_rgba(255,107,53,0.4)] hover:shadow-[0_0_50px_rgba(255,107,53,0.6)] hover:scale-105 active:scale-95 transition-transform duration-200 text-sm sm:text-base"
              data-testid="landing-register-btn"
            >
              Rejoindre ReallyFan — C'est gratuit
            </button>
            <button
              onClick={() => navigate("/auth")}
              className="bg-foreground/5 backdrop-blur-md text-foreground font-medium px-6 sm:px-8 py-3.5 rounded-full border border-foreground/10 hover:bg-foreground/10 transition-colors duration-200 text-sm sm:text-base"
              data-testid="landing-signin-btn"
            >
              J'ai deja un compte
            </button>
          </div>
        </motion.div>

        {/* How credits work - KEY section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-5 sm:p-8 mb-10 sm:mb-16"
          data-testid="credits-explanation"
        >
          <h2 className="font-heading font-bold text-lg sm:text-xl text-foreground mb-5 text-center">
            Comment ca marche ? <span className="text-[#FF6B35]">C'est simple et gratuit.</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                <Coins className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="font-heading font-bold text-sm text-foreground mb-1">1. Inscris-toi</p>
              <p className="text-xs text-muted-foreground">Recois immediatement <strong className="text-emerald-400">100 credits gratuits</strong>. Aucun paiement requis, jamais.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#FF6B35]/15 flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <p className="font-heading font-bold text-sm text-foreground mb-1">2. Sois actif</p>
              <p className="text-xs text-muted-foreground">Chaque like, commentaire, defi ou connexion quotidienne te rapporte des credits supplementaires.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#7000FF]/15 flex items-center justify-center mx-auto mb-3">
                <Gift className="w-6 h-6 text-[#7000FF]" />
              </div>
              <p className="font-heading font-bold text-sm text-foreground mb-1">3. Profite</p>
              <p className="text-xs text-muted-foreground">Utilise tes credits pour faire monter tes artistes et gagner des goodies exclusifs aux encheres.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5 text-[#FF6B35]" />
                <p className="font-heading font-bold text-sm text-foreground">Pour les Fans</p>
              </div>
              <p className="text-xs text-[#AFA8BA] leading-relaxed">
                Fais monter tes artistes preferes dans le classement. Encheris sur des goodies exclusifs (dedicaces, merch, places VIP...) avec tes credits gagnes gratuitement.
              </p>
            </div>
            <div className="bg-[#7000FF]/10 border border-[#7000FF]/20 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Ticket className="w-5 h-5 text-[#7000FF]" />
                <p className="font-heading font-bold text-sm text-foreground">Pour les Artistes</p>
              </div>
              <p className="text-xs text-[#AFA8BA] leading-relaxed">
                Utilise tes credits pour booster ta visibilite dans le feed. Propose des goodies aux encheres pour engager tes fans et accelerer ta croissance.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Equalizer decoration */}
        <div className="flex items-end gap-1.5 justify-center mb-10 sm:mb-16 opacity-40">
          {[0.3, 0.7, 1, 0.5, 0.8, 0.4, 0.9, 0.6, 1, 0.3, 0.7, 0.5].map((h, i) => (
            <div
              key={i}
              className="w-1.5 bg-gradient-to-t from-[#FF6B35] to-[#FF8FA3] rounded-full animate-wave"
              style={{
                height: `${h * 40}px`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-16"
        >
          {features.map((f, i) => (
            <div
              key={i}
              className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-3xl p-5 sm:p-6 hover:border-foreground/10 transition-colors duration-300"
            >
              <div className="w-10 h-10 rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <h3 className="font-heading font-bold text-sm sm:text-base text-foreground mb-2">{f.title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="text-center mb-10 sm:mb-16"
        >
          <p className="text-base sm:text-lg text-[#AFA8BA] mb-5">
            Pret a rejoindre la communaute ? <strong className="text-emerald-400">C'est 100% gratuit.</strong>
          </p>
          <button
            onClick={() => navigate("/auth?tab=register")}
            className="bg-[#FF6B35] text-white font-bold px-8 sm:px-10 py-4 rounded-full shadow-[0_0_30px_rgba(255,107,53,0.4)] hover:shadow-[0_0_50px_rgba(255,107,53,0.6)] hover:scale-105 active:scale-95 transition-transform duration-200 text-sm sm:text-base"
            data-testid="landing-register-bottom-btn"
          >
            Creer mon compte gratuit
          </button>
        </motion.div>

        {/* Footer */}
        <footer className="text-center pb-8">
          <p className="text-[10px] sm:text-xs text-muted-foreground">
            Les credits ReallyFan sont des droits d'usage. Ils ne sont pas convertibles en argent reel.
          </p>
        </footer>
      </div>

      {/* Noise overlay */}
      <div className="noise-overlay" />
    </div>
  );
}
